import { useCallback, useEffect, useMemo, useState, useContext } from "react";
import clsx from "clsx";

import { Button, ButtonBar, LoadingIndicator } from "@k8slens/lds";
import { FormSwitchGroup } from "@k8slens/lds-form";
import { SwitchValue } from "@k8slens/lds-form/lib/es/FormSwitchGroup/FormSwitchGroup";

import { useBusinessFeatures } from "src/hooks/useBusinessFeatures";
import { AIRGAPPED, OFFLINE_ACTIVATION } from "src/constants";
import { useIsControlCenterEnabled } from "src/hooks/useIsControlCenterEnabled";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useAnalytics } from "src/hooks/useAnalytics";

import { BusinessContext } from "src/components/Business/Base";
import { useConfirmDialog } from "src/components/ConfirmDialog/ConfirmDialog";
import Notification from "src/components/Notification/Notification";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

import styles from "./FeatureManagement.module.css";

const featureDescriptions: { [key: string]: string } = {
  [AIRGAPPED]:
    "Lens Desktop by default uses internet connections to access services such as auto-updating, auto-installation of tools, getting updates from Helm repositories or CVE databases, or accessing the support chat. In air-gapped or highly regulated environments this can create unwanted network traffic. Air-gapped mode allows you to centrally configure Lens Desktop for all users to stop all outbound internet traffic to assure network security. If Air-gapped mode is enabled Offline Activation will automatically be enabled for your Lens Business ID.",
};

const hiddenFeatures = [OFFLINE_ACTIVATION];

const FeatureManagement = () => {
  const { ConfirmDialog, confirm } = useConfirmDialog();

  const { businessId } = useContext(BusinessContext);
  const { loading: loadingSubscriptions, businessSubscriptions } = useGetBusinessSubscriptions(businessId);
  const { controlCenterEnabled, loading } = useIsControlCenterEnabled(
    loadingSubscriptions ? undefined : businessSubscriptions,
  );
  const { track } = useAnalytics();

  const {
    businessFeatures,
    loadingBusinessFeatures,
    updateBusinessFeatures,
    updatingBusinessFeatures,
    errorLoadingBusinessFeatures,
    errorUpdatingBusinessFeatures,
  } = useBusinessFeatures(businessId);

  const [switches, setSwitches] = useState<Array<SwitchValue>>([]);

  const onSave = useCallback(() => {
    if (!businessId || !switches) {
      return;
    }

    const features = switches.map(({ id, checked }) => ({
      key: id,
      enabled: Boolean(checked),
    }));

    const offlineActivation = businessFeatures?.find((d) => d.key === OFFLINE_ACTIVATION);

    // Set Offline Activation to enabled if Airgapped is enabled
    if (switches.find((d) => d.id === AIRGAPPED && d.checked) && offlineActivation) {
      offlineActivation.enabled = true;
    }

    track("Update Lens Pro Features");
    updateBusinessFeatures({
      businessId,
      features: offlineActivation
        ? [...features, { key: offlineActivation.key, enabled: offlineActivation.enabled }]
        : features,
    });
  }, [businessFeatures, businessId, switches, updateBusinessFeatures, track]);

  useEffect(() => {
    if (!businessFeatures) {
      return;
    }

    const switches = businessFeatures
      .map(
        ({ key, name, description, enabled }) => ({
          id: key,
          label: name,
          description: featureDescriptions[key] || description,
          checked: enabled,
        }),
        [],
      )
      .filter(({ id }) => !hiddenFeatures.includes(id));

    setSwitches(switches);
  }, [businessFeatures]);

  const hasChanges = useMemo(() => {
    if (!businessFeatures || !switches) {
      return false;
    }

    return switches.some(({ id, checked }) => {
      const feature = businessFeatures.find((feature) => feature.key === id);

      return feature?.enabled !== checked;
    });
  }, [businessFeatures, switches]);

  const isLoading = loading || loadingBusinessFeatures;

  const handleSwitchChange = useCallback(
    async (nextSwitches: Array<SwitchValue>, keys: Array<string>) => {
      const changed = nextSwitches.filter(({ id }) => keys.includes(id));

      if (changed.find((d) => d.id === AIRGAPPED && d.checked)) {
        // If user turns on the Airgapped mode, show a confirmation dialog
        const confirmed = await confirm(
          "Are you sure you want to enable airgapped mode?",
          <>
            <p>
              {/* eslint-disable-next-line max-len */}
              Air-gapped mode requires <strong>Offline Activation</strong> to be enabled and users in your Lens Business
              ID will be able to download <strong>Offline Activation Codes</strong>.
            </p>
            <p className="!mb-0">
              {/* eslint-disable-next-line max-len */}
              The associated Lens Subscription of the user will be fixed until the end of the billing cycle and you
              cannot re-assign the seat to other users.
            </p>
          </>,
          { confirmText: "Enable" },
        );

        if (confirmed) {
          setSwitches(nextSwitches);
        }
      } else {
        setSwitches(nextSwitches);
      }
    },
    [confirm],
  );

  return (
    <div className={styles.featureManagement}>
      <header>
        <h2 className={clsx("lds-h2-caps", styles.welcome)} id="feature-settings">
          Feature Management
        </h2>
        <p>
          {/* eslint-disable-next-line max-len */}
          Lens Pro includes additional features of Lens Desktop Kubernetes, Lens Security Center, Lens Teamwork, and
          Lens Support. Centralized feature management allows you to enable or disable these Lens Pro features across
          all users within their Lens Business ID.
        </p>
      </header>
      {isLoading && <LoadingIndicator size="2xl" />}
      {!isLoading && (
        <>
          {controlCenterEnabled && (errorLoadingBusinessFeatures || errorUpdatingBusinessFeatures) && (
            <Notification
              type="flash"
              level="error"
              message={errorLoadingBusinessFeatures?.message || errorUpdatingBusinessFeatures?.message}
            />
          )}
          {!controlCenterEnabled && (
            <Notification
              level="info"
              className={styles.enterpriseAccNotification}
              message={
                <>
                  {"Please "}
                  <TrackedAnchor href="https://k8slens.dev/purchase-request" target="_blank" rel="noreferrer">
                    contact sales
                  </TrackedAnchor>
                  {" to access Lens Control Center features."}
                </>
              }
            />
          )}
          <section
            className={clsx({
              [styles.disabledSection]: !controlCenterEnabled,
            })}
          >
            <form className={styles.form}>
              <div className={styles.switchGroupWrapper}>
                <FormSwitchGroup
                  wrapperProps={{ role: "group", "aria-labelledby": "feature-settings" }}
                  label=""
                  values={switches || []}
                  onChange={handleSwitchChange}
                  disabled={!controlCenterEnabled || updatingBusinessFeatures}
                />
              </div>
              <ButtonBar type="grid" className={styles.buttonBar}>
                <Button
                  primary
                  disabled={!hasChanges || loadingBusinessFeatures || updatingBusinessFeatures}
                  loading={updatingBusinessFeatures}
                  onClick={onSave}
                  label="Update features"
                />
              </ButtonBar>
            </form>
          </section>
        </>
      )}
      {ConfirmDialog}
    </div>
  );
};

export default FeatureManagement;
